<template>
  <div v-if="isVisible" class="proform-advance-modal-root">
    <div class="modal-backdrop fade show" @click="close"></div>
    <div class="modal fade show" tabindex="-1" @click.self="close">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-percentage me-2 text-primary" aria-hidden="true"></i>
              {{
                hasCurrent
                  ? $t("financialSummary.stages.advanceModalEditTitle")
                  : $t("financialSummary.stages.advanceModalSetTitle")
              }}
            </h5>
            <button type="button" class="btn-close" aria-label="Close" @click="close"></button>
          </div>
          <div class="modal-body">
            <div class="btn-group w-100 mb-3" role="group">
              <button
                type="button"
                class="btn btn-sm"
                :class="mode === 'pct' ? 'btn-primary' : 'btn-outline-primary'"
                @click="setMode('pct')"
              >
                {{ $t("financialSummary.stages.advanceModePct") }}
              </button>
              <button
                type="button"
                class="btn btn-sm"
                :class="mode === 'amount' ? 'btn-primary' : 'btn-outline-primary'"
                @click="setMode('amount')"
              >
                {{ $t("financialSummary.stages.advanceModeAmount") }}
              </button>
            </div>

            <div class="input-group input-group-sm">
              <span class="input-group-text">{{ mode === "pct" ? "%" : moneyPrefix }}</span>
              <input
                ref="valueInput"
                :value="draft"
                type="number"
                step="any"
                min="0"
                class="form-control"
                :disabled="saving"
                @input="onValueInput($event.target.value)"
                @keyup.enter="submit"
              />
            </div>
            <div class="form-text small mb-3">{{ previewText }}</div>

            <label class="form-label small mb-1">
              {{ $t("financialSummary.stages.advancePayableLabel") }}
            </label>
            <InputList
              :field="payableField"
              :value="payableDraft"
              field-id="proform-advance-payable"
              :list-options="payableOptions"
              :disabled="saving"
              @input="payableDraft = $event"
            />

            <div v-if="errorText" class="alert alert-warning py-2 px-2 small mb-0 mt-3">
              {{ errorText }}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" :disabled="saving" @click="close">
              {{ $t("financialSummary.close") }}
            </button>
            <button type="button" class="btn btn-primary" :disabled="saving" @click="submit">
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
              {{ $t("financialSummary.stages.advancePctSave") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InputList from "./inputs/InputList.vue";

/**
 * Configura el adelanto de la proforma en un solo lugar: porcentaje o monto, más la
 * condición de pago.
 *
 * El monto no viaja como monto: se convierte al porcentaje equivalente y se emite eso,
 * porque `advance` es de donde todo el resto del sistema calcula. Así el porcentaje y el
 * monto no pueden contradecirse. La conversión va sin redondear a propósito: recortada a
 * seis decimales el monto reconstruido se desviaba en milésimas visibles.
 */
export default {
  name: "ProformAdvanceModal",
  components: { InputList },
  props: {
    isVisible: { type: Boolean, default: false },
    totalProform: { type: Number, default: 0 },
    currentPct: { type: Number, default: 0 },
    currentPayable: { type: String, default: "" },
    moneyPrefix: { type: String, default: "$" },
    payableOptions: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      mode: "pct",
      draft: "",
      /**
       * Fuente de verdad del adelanto, siempre en porcentaje y sin redondear. El campo
       * muestra el % recortado a 2 decimales o el monto, pero lo que se guarda sale de
       * acá: así cambiar de modo o guardar sin tocar nada no corre el valor.
       */
      exactPct: NaN,
      payableDraft: null,
      errorText: "",
      payableField: {
        key: "payable",
        label: "",
        type: "list",
        listType: "",
        required: false,
      },
    };
  },
  computed: {
    hasCurrent() {
      return Number(this.currentPct) > 0;
    },
    /** Contracara de lo que se escribe: si va %, el monto; si va monto, el %. */
    previewText() {
      const total = Number(this.totalProform) || 0;
      const pct = Number(this.exactPct);
      if (!Number.isFinite(pct) || pct <= 0 || total <= 0) return "";
      return this.mode === "pct"
        ? this.$t("financialSummary.stages.advancePreviewFromPct", {
            amount: this.moneyPrefix + this.formatAmount((total * pct) / 100),
          })
        : this.$t("financialSummary.stages.advancePreviewFromAmount", {
            pct: this.formatPct(pct),
          });
    },
  },
  watch: {
    isVisible(open) {
      if (open) this.reset();
    },
  },
  methods: {
    reset() {
      this.mode = "pct";
      this.exactPct = this.hasCurrent ? Number(this.currentPct) : NaN;
      this.draft = this.hasCurrent ? this.formatPct(this.exactPct) : "";
      this.payableDraft =
        (this.payableOptions || []).find((o) => String(o._id) === String(this.currentPayable)) || null;
      this.errorText = "";
      this.focusValue();
    },
    focusValue() {
      this.$nextTick(() => {
        if (this.$refs.valueInput) this.$refs.valueInput.focus();
      });
    },
    onValueInput(raw) {
      this.draft = raw;
      this.errorText = "";
      const total = Number(this.totalProform) || 0;
      const n = Number(String(raw).trim());
      if (!Number.isFinite(n)) {
        this.exactPct = NaN;
        return;
      }
      this.exactPct = this.mode === "pct" ? n : total > 0 ? (n / total) * 100 : NaN;
    },
    /** Cambiar de modo convierte lo que había; el % exacto no se toca. */
    setMode(mode) {
      if (this.mode === mode) return;
      this.mode = mode;
      this.errorText = "";
      const total = Number(this.totalProform) || 0;
      const pct = Number(this.exactPct);
      if (!Number.isFinite(pct) || pct <= 0 || (mode === "amount" && total <= 0)) {
        this.draft = "";
        this.focusValue();
        return;
      }
      this.draft =
        mode === "pct" ? this.formatPct(pct) : this.trimNumber((total * pct) / 100);
      this.focusValue();
    },
    /** Id de la condición elegida, solo si es una de la lista (el multiselect puede taggear). */
    selectedPayableId() {
      const id = this.payableDraft && this.payableDraft._id ? String(this.payableDraft._id) : "";
      if (!id) return null;
      return (this.payableOptions || []).some((o) => String(o._id) === id) ? id : null;
    },
    formatPct(pct) {
      const n = Number(pct);
      if (!Number.isFinite(n)) return "0";
      return String(Math.round(n * 1e2) / 1e2);
    },
    /** Para el input numérico: sin separadores de miles y sin ceros de relleno. */
    trimNumber(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return "";
      return String(Math.round(n * 1e4) / 1e4);
    },
    formatAmount(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return "0.00";
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    },
    close() {
      if (this.saving) return;
      this.$emit("close");
    },
    submit() {
      if (this.saving) return;
      const total = Number(this.totalProform) || 0;
      const pct = Number(this.exactPct);
      const payable = this.selectedPayableId();

      if (!Number.isFinite(pct) || pct <= 0) {
        this.errorText = this.$t("financialSummary.stages.advanceValueInvalid");
        return;
      }
      if (this.mode === "amount" && total <= 0) {
        this.errorText = this.$t("financialSummary.stages.advanceAmountNoTotal");
        return;
      }
      if (pct > 100) {
        this.errorText =
          this.mode === "pct"
            ? this.$t("financialSummary.stages.advancePctInvalid")
            : this.$t("financialSummary.stages.advanceAmountOverTotal", {
                total: this.moneyPrefix + this.formatAmount(total),
              });
        return;
      }
      this.errorText = "";
      this.$emit("save", { pct, payable });
    },
  },
};
</script>

<style scoped>
.proform-advance-modal-root .modal {
  display: block;
}
</style>
